import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import connector from 'components/@home/@settings/Tabs/connector';
import SectionBody from 'components/@home/@settings/Tabs/SectionBody';
import SectionTitle from 'components/@home/@settings/Tabs/SectionTitle';
import DeleteDialog from 'components/common/DeleteDialog';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import EditIcon from 'mdi-react/EditIcon';
import { bool, object, objectOf } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { I18n } from 'utils/i18n';
import appShape from 'shapes/appShape';
import companyShape from 'shapes/company';
import userShape from 'shapes/user';
import { invalidateEmployeesTags } from 'store/app/entities/EmployeesApiSlice';
import initialsFromUsername from 'utils/initialsFromUsername';
import openAppConfig from 'utils/openAppConfig';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  card: {
    width: 400,
    display: 'flex',
    flexDirection: 'column',
    margin: '15px',
    position: 'relative',
  },
  content: {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  description: {
    flex: '1 0 auto',
  },
  titleWrapper: {
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  title: {
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  avatar: {
    fontSize: 28,
    width: 80,
    height: 80,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.secondary.light,
    marginBottom: theme.spacing(3),
  },
  noAvatar: {
    backgroundColor: theme.palette.primary.main,
  },
  check: {
    marginLeft: '.5ex',
    color: theme.palette.tertiary.main,
    background: theme.palette.background.paper,
    borderRadius: '50%',
  },
  cardDots: {
    position: 'absolute',
    right: '5px',
    top: '5px',
  },
  installed: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    left: '16px',
    top: '16px',
    '& span': {
      marginLeft: '4px',
    },
  },
  backGround: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: 55,
    width: '100%',
    backgroundColor: theme.palette.secondary.ultraLight,
  },
  danger: {
    color: theme.palette.danger,
  },
});

const AppsSettings = ({
  actions,
  apps = [],
  authUser,
  authCompany,
  isCompanyAdmin,
  isTeamAdmin,
}) => {
  const [appToDelete, setAppToDelete] = useState(null);
  const [appToUninstall, setAppToUninstall] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const dispatch = useDispatch();

  const getAnchorEl = () => menuData?.target;

  const closeMenu = () => {
    setMenuData(null);
  };

  const openMenu = (event, app) => {
    setMenuData({ target: event.target, app });
  };

  const installApp = (app, install = !app.installed, force = false) => {
    if (install || force) {
      actions.apps.install(app._id, install).then(() => {
        dispatch(invalidateEmployeesTags([{ type: 'Employee', _id: 'LIST' }]));
      });
    } else if (!install) {
      setAppToUninstall(app);
    }
  };

  const editApp = () => {
    actions.drawers.app.open(menuData?.app);
    setMenuData(null);
  };

  const deleteApp = () => {
    if (menuData) {
      setAppToDelete(menuData.app);
      setMenuData(null);
    }
  };

  const confirmDelete = () => {
    actions.apps.destroy(appToDelete._id).then(() => {
      dispatch(invalidateEmployeesTags([{ type: 'Employee', _id: 'LIST' }]));
    });
    setAppToDelete(null);
  };

  const confirmUninstall = () => {
    setAppToUninstall(null);
    installApp(appToUninstall, false, true);
  };

  const closeDialog = () => {
    setAppToDelete(null);
  };

  const closeUninstallDialog = () => {
    setAppToUninstall(null);
  };

  useEffect(() => {
    actions.apps.load();
  }, [actions.apps]);

  return (
    <Box sx={styles.root}>
      <SectionTitle title={I18n.t('SettingsMenu.apps')}>
        <Button onClick={editApp} variant="contained" color="primary">
          {I18n.t('AppsSettings.New')}
        </Button>
      </SectionTitle>
      <SectionBody>
        <Box sx={styles.grid}>
          {Object.entries(apps).map(([key, app]) => (
            <Card elevation={3} key={key} sx={styles.card}>
              <CardContent sx={styles.content}>
                <Box sx={styles.backGround} />
                {app.installed ? (
                  <Box sx={styles.installed}>
                    <Box
                      component="span"
                      sx={[
                        {
                          display: 'inline-flex',
                        },
                        styles.check,
                      ]}
                    >
                      <CheckCircleIcon />
                    </Box>
                    <Typography variant="caption" component="span">
                      {I18n.t('AppsSettings.installed')}
                    </Typography>
                  </Box>
                ) : null}
                {app.creator === authUser._id ||
                (app.company === authCompany._id && isCompanyAdmin) ? (
                  <Box sx={styles.cardDots}>
                    <IconButton onClick={event => openMenu(event, app)} size="large">
                      <DotsVerticalIcon />
                    </IconButton>
                  </Box>
                ) : null}
                <Avatar
                  aria-label="avatar"
                  src={app.avatar}
                  sx={[styles.avatar, !app.avatar && styles.noAvatar]}
                >
                  {initialsFromUsername(app.name)}
                </Avatar>
                <Typography variant="h4" sx={styles.titleWrapper}>
                  <Box component="span" sx={styles.title}>
                    {app.name}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  sx={styles.description}
                >
                  {app.description}
                </Typography>
              </CardContent>
              {isTeamAdmin ? (
                <CardActions>
                  <Grid container spacing={2}>
                    {isCompanyAdmin && (
                      <Grid size="auto">
                        <Button
                          sx={app.installed ? styles.danger : undefined}
                          variant="outlined"
                          color={app.installed ? 'inherit' : 'primary'}
                          onClick={() => installApp(app)}
                        >
                          {I18n.t(`AppsSettings.${app.installed ? 'un' : ''}install`)}
                        </Button>
                      </Grid>
                    )}
                    {app.installed && app.configPageUrl ? (
                      <Grid size="auto">
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={openAppConfig(actions, app._id)}
                        >
                          {I18n.t(`AppsSettings.Configure`)}
                        </Button>
                      </Grid>
                    ) : null}
                  </Grid>
                </CardActions>
              ) : null}
            </Card>
          ))}
        </Box>
      </SectionBody>
      <Menu anchorEl={getAnchorEl} keepMounted open={!!menuData} onClose={closeMenu}>
        <MenuItem onClick={editApp}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <Typography variant="inherit">{I18n.t('AppsSettings.Edit')}</Typography>
        </MenuItem>
        <MenuItem onClick={deleteApp}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <Typography variant="inherit">{I18n.t('AppsSettings.Delete')}</Typography>
        </MenuItem>
      </Menu>
      <DeleteDialog
        typeBack={appToDelete?.name}
        message={I18n.t('AppsSettings.ConfirmDelete', { name: appToDelete?.name })}
        isOpen={appToDelete}
        onConfirm={confirmDelete}
        onClose={closeDialog}
      />
      <DeleteDialog
        confirmText={I18n.t('AppsSettings.uninstall')}
        typeBack={appToUninstall?.name}
        message={I18n.t('AppsSettings.ConfirmUninstallTitle', { name: appToUninstall?.name })}
        isOpen={appToUninstall}
        onConfirm={confirmUninstall}
        onClose={closeUninstallDialog}
      >
        <Typography>
          {I18n.t('AppsSettings.ConfirmUninstallBody', { name: appToUninstall?.name })}
        </Typography>
      </DeleteDialog>
    </Box>
  );
};

AppsSettings.propTypes = {
  isCompanyAdmin: bool.isRequired,
  isTeamAdmin: bool.isRequired,
  authCompany: companyShape.isRequired,
  authUser: userShape.isRequired,
  actions: object.isRequired,
  apps: objectOf(appShape),
};

export default connector(AppsSettings);
